import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AclPermissionDetail } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { connectState, InputObservable } from '@y42/angular/util';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { FeatureAggregationService } from '@y42/feature';
import { GoJSLegendPosition, legendsByColorScheme } from '@y42/feature/gojs/legend';
import { GojsContextMenuService, GoJsOverviewPosition } from '@y42/gojs';
import { Y42FeatureType } from '@y42/json-schema';
import { map, merge, Observable, tap } from 'rxjs';
import { OrchestrationNodeLogsDialogService } from '../orchestration-node-logs-dialog/orchestration-node-logs-dialog.service';
import { OrchestrationDiagramConfig, OrchestrationDiagramUIModel } from './models/orchestration-diagram.model';
import { OrchestrationDiagramToolbarService } from './orchestration-diagram-toolbar/orchestration-diagram-toolbar.service';
import { OrchestrationDiagramDataService } from './services/orchestration-diagram-data.service';
import { OrchestrationDiagramHydrationService } from './services/orchestration-diagram-hydration.service';
import { OrchestrationDiagramService } from './services/orchestration-diagram.service';

@UntilDestroy()
@Component({
  selector: 'y42-orchestration-diagram[diagramConfig]',
  templateUrl: './orchestration-diagram.component.html',
  styleUrls: ['./orchestration-diagram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GojsContextMenuService,
    OrchestrationDiagramService,
    OrchestrationDiagramDataService,
    OrchestrationDiagramHydrationService,
    OrchestrationDiagramToolbarService,
    OrchestrationNodeLogsDialogService,
  ],
})
export class OrchestrationDiagramComponent {
  constructor(
    private readonly orchestrationDiagramService: OrchestrationDiagramService,
    private readonly userColorSchemeService: UserColorSchemeService,
    private readonly featureAggregationService: FeatureAggregationService,
  ) {
    merge(this.emitOutputModelOnChange(), this.emitRedirectAssetOnChange()).pipe(untilDestroyed(this)).subscribe();
  }

  @Input() set diagramConfig(config: OrchestrationDiagramConfig) {
    this.orchestrationDiagramService.diagramConfig = config;
  }

  @Input()
  @InputObservable()
  isOrchestrationEditable = createAclPermissionDetail({ allowed: false });
  private readonly isOrchestrationEditable$!: Observable<AclPermissionDetail>;

  @Output() outputModel = new EventEmitter<OrchestrationDiagramUIModel>();
  @Output() redirectToAsset = new EventEmitter<string>();

  readonly orchestrationIcon = this.featureAggregationService.getLogoByFeatureType(Y42FeatureType.Orchestration);
  readonly attentionNodePredicates = [];

  readonly diagram = this.orchestrationDiagramService.diagram;

  readonly state = connectState(this, {
    isOrchestrationEditable: this.isOrchestrationEditable$,
    isDiagramEditable: this.orchestrationDiagramService.isEditable$,
    diagramLoading: this.orchestrationDiagramService.loading$,
    gojsOverviewPosition: this.orchestrationDiagramService.isEditable$.pipe(
      map((isDiagramEditable) =>
        isDiagramEditable ? GoJsOverviewPosition.RightWithSidebar : GoJsOverviewPosition.Right,
      ),
    ),
    gojsLegendPosition: this.orchestrationDiagramService.isEditable$.pipe(
      map((isDiagramEditable) => {
        const position: GoJSLegendPosition = isDiagramEditable ? 'right-with-sidebar' : 'right-without-sidebar';
        return position;
      }),
    ),
    legends: this.userColorSchemeService.hasDarkMode$.pipe(
      map((hasDarkMode) => {
        const allLegends = legendsByColorScheme(hasDarkMode);

        return [
          allLegends[Y42FeatureType.Integration],
          allLegends[Y42FeatureType.UiModel],
          allLegends[Y42FeatureType.SqlModel],
          allLegends[Y42FeatureType.Export],
        ];
      }),
    ),
  });

  private emitOutputModelOnChange() {
    return this.orchestrationDiagramService.outputModel$.pipe(tap((outputModel) => this.outputModel.emit(outputModel)));
  }

  private emitRedirectAssetOnChange() {
    return this.orchestrationDiagramService.redirectToAsset$.pipe(tap((assetId) => this.redirectToAsset.emit(assetId)));
  }
}
